import React, { useState, useMemo, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { Building } from '../../../../../types/protocols';
import { BaseType } from '../../../../../types/Enums';
import { translate } from '../../../../../types/EnumTranslations';
import { ComponentLifetime, TemplatesByType } from '../../../TemplatesEditor/types';
import { useBuildComponentTypes } from '../../../../../contexts/BuildComponentTypesContext';
import {
    generateYearlyPlansForBuilding,
    CostSource,
    COST_SOURCE_LABELS,
    MaintenancePlan,
    ActivityType
} from '../../../../../Helpers/maintenanceCostUtils';
import { getChartColor } from '../../../../../Helpers/chartColors';
import { BASE_TYPE_GROUPS, presentBaseTypeGroupLabel } from '../../../../../Helpers/baseTypeUtils';
import type { MaintenanceDraftWithDone } from '../../types';
import { CombinedTimeline, ChartTimelineLegend } from './CombinedTimeline';
import { findPreviewGaps } from '../../utils/previewGaps';
import '../modals/DraftPreviewModal.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

interface DraftPreviewContentProps {
    building: Building;
    maintenanceDraft: MaintenanceDraftWithDone;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    /** If true, shows filters in header. If false, uses compact mode for inline display. */
    showHeader?: boolean;
    /** The currently active maintenance plan for comparison (optional) */
    activePlan?: MaintenancePlan | null;
    /** Controlled year window, shared with the diff; uncontrolled when absent */
    yearsToShow?: number;
    onYearsToShowChange?: (years: number) => void;
}

interface ActivityWithDetails {
    componentKey: string;
    componentName: string;
    activityType: ActivityType;
    activityName: string;
    cost: number;
    costSource?: CostSource;
    costCalculation: string;
    costComment?: string;
    investmentPercentage?: number;
    comment?: string;
}

interface BaseTypeGroup {
    baseType: string;
    label: string;
    totalCost: number;
    activities: ActivityWithDetails[];
}

interface YearData {
    year: number;
    totalCost: number;
    hasActivities: boolean;
    baseTypeGroups: BaseTypeGroup[];
}

/**
 * Reusable preview content for MaintenanceDraft.
 * Used both in the modal and in the inline preview pane.
 */
export const DraftPreviewContent: React.FC<DraftPreviewContentProps> = ({
    building,
    maintenanceDraft,
    componentLifetimes,
    maintenanceTemplates,
    showHeader = true,
    activePlan,
    yearsToShow: controlledYears,
    onYearsToShowChange
}) => {
    const { types: buildComponentTypes } = useBuildComponentTypes();
    const [ownYears, setOwnYears] = useState<number>(10);
    const yearsToShow = controlledYears ?? ownYears;
    const setYearsToShow = onYearsToShowChange ?? setOwnYears;
    const [costTypeFilter, setCostTypeFilter] = useState<'all' | 'maintenance' | 'replacement'>('all');
    const [expandedYears, setExpandedYears] = useState<Set<number>>(() => new Set());
    const [compareToActive, setCompareToActive] = useState<boolean>(false);
    const [gapsExpanded, setGapsExpanded] = useState<boolean>(false);
    const [tooltip, setTooltip] = useState<{
        content: React.ReactNode;
        x: number;
        y: number;
    } | null>(null);

    // Helper to calculate cost based on filter and investment percentage
    const getFilteredCost = useCallback((cost: number, investmentPercentage?: number): number => {
        const pct = investmentPercentage ?? 0;
        if (costTypeFilter === 'all') return cost;
        if (costTypeFilter === 'replacement') return cost * pct / 100; // Investering
        return cost * (100 - pct) / 100; // Underhåll
    }, [costTypeFilter]);

    // Generate yearly plans and pre-compute all data
    const { yearsData, grandTotal, firstYearWithActivities } = useMemo(() => {
        if (!building?.model?.structure?.components || !maintenanceDraft) {
            return { yearsData: [], grandTotal: 0, firstYearWithActivities: null };
        }

        const yearlyPlans = generateYearlyPlansForBuilding({
            building,
            plan: maintenanceDraft,
            componentLifetimes,
            maintenanceTemplates,
            yearsToShow,
            buildingName: building.summary?.name,
            buildComponentTypes,
            filterIsDone: true,
            includeEmptyYears: true,
            isDraft: true
        });

        let total = 0;
        let firstWithActivities: number | null = null;

        const data: YearData[] = yearlyPlans.map(yearPlan => {
            // Calculate filtered costs for all activities
            const activitiesWithFilteredCosts = yearPlan.activities
                .map(a => ({
                    ...a,
                    filteredCost: getFilteredCost(a.cost, a.investmentPercentage)
                }))

            const yearTotal = activitiesWithFilteredCosts.reduce((sum, a) => sum + a.filteredCost, 0);
            total += yearTotal;

            const hasActivities = activitiesWithFilteredCosts.length > 0;
            if (hasActivities && firstWithActivities === null) {
                firstWithActivities = yearPlan.year;
            }

            // Pre-compute baseType groups
            const groups: Record<string, BaseTypeGroup> = {};
            activitiesWithFilteredCosts.forEach(activity => {
                const baseType = activity.baseType;
                if (!groups[baseType]) {
                    groups[baseType] = {
                        baseType,
                        label: translate(baseType as BaseType),
                        totalCost: 0,
                        activities: []
                    };
                }
                groups[baseType].totalCost += activity.filteredCost;
                groups[baseType].activities.push({
                    componentKey: activity.componentKey,
                    componentName: activity.componentName,
                    activityType: activity.activityType,
                    activityName: activity.activityName,
                    cost: activity.filteredCost,
                    costSource: activity.costSource,
                    costCalculation: activity.costCalculation,
                    costComment: activity.costComment,
                    investmentPercentage: activity.investmentPercentage,
                    comment: activity.comment
                });
            });

            return {
                year: yearPlan.year,
                totalCost: yearTotal,
                hasActivities,
                baseTypeGroups: Object.values(groups).sort((a, b) => a.label.localeCompare(b.label, 'sv'))
            };
        });

        return { yearsData: data, grandTotal: total, firstYearWithActivities: firstWithActivities };
    }, [building, maintenanceDraft, componentLifetimes, maintenanceTemplates, yearsToShow, costTypeFilter, buildComponentTypes]);

    // Generate yearly data for active plan (for comparison)
    const { activeYearsData, activeGrandTotal } = useMemo(() => {
        if (!activePlan || !building?.model?.structure?.components || !compareToActive) {
            return { activeYearsData: [], activeGrandTotal: 0 };
        }

        const yearlyPlans = generateYearlyPlansForBuilding({
            building,
            plan: activePlan,
            componentLifetimes,
            maintenanceTemplates,
            yearsToShow,
            buildingName: building.summary?.name,
            buildComponentTypes,
            filterIsDone: false, // Don't filter isDone for active plan
            includeEmptyYears: true
        });

        let total = 0;
        const data = yearlyPlans.map(yearPlan => {
            const yearTotal = yearPlan.activities.reduce((sum, a) =>
                sum + getFilteredCost(a.cost, a.investmentPercentage), 0);
            total += yearTotal;
            return {
                year: yearPlan.year,
                totalCost: yearTotal
            };
        });

        return { activeYearsData: data, activeGrandTotal: total };
    }, [activePlan, building, componentLifetimes, maintenanceTemplates, yearsToShow, buildComponentTypes, compareToActive, getFilteredCost]);

    // Done components the window hides: their first activity lies past it, or they have none.
    // Listed so a facade with a 40-year lifetime does not look like it went missing.
    const previewGaps = useMemo(() => {
        if (!building?.model?.structure?.components || !maintenanceDraft) return [];
        return findPreviewGaps({ building, draft: maintenanceDraft, componentLifetimes, maintenanceTemplates, yearsToShow });
    }, [building, maintenanceDraft, componentLifetimes, maintenanceTemplates, yearsToShow]);

    // Set initial expanded year
    useEffect(() => {
        if (firstYearWithActivities !== null && expandedYears.size === 0) {
            setExpandedYears(new Set([firstYearWithActivities]));
        }
    }, [firstYearWithActivities]);

    // Toggle year expansion
    const toggleYear = useCallback((year: number) => {
        setExpandedYears(prev => {
            const next = new Set(prev);
            if (next.has(year)) {
                next.delete(year);
            } else {
                next.add(year);
            }
            return next;
        });
    }, []);

    // Build chart datasets - comparison mode shows draft vs active, normal mode shows stacked by component type
    const chartDatasets = useMemo(() => {
        // Comparison mode: show draft vs active plan side by side
        if (compareToActive && activeYearsData.length > 0) {
            return [
                {
                    label: 'Aktiv plan',
                    data: yearsData.map(yearData => {
                        const activeYear = activeYearsData.find(a => a.year === yearData.year);
                        return activeYear?.totalCost || 0;
                    }),
                    backgroundColor: '#6c757d', // Gray for active plan
                    borderRadius: 0,
                    borderSkipped: false as const,
                },
                {
                    label: 'Utkast',
                    data: yearsData.map(yearData => yearData.totalCost),
                    backgroundColor: '#0d6efd', // Blue for draft
                    borderRadius: 0,
                    borderSkipped: false as const,
                }
            ];
        }

        // Normal mode: stacked bars by component type in canonical BASE_TYPE_GROUPS order
        const activeBaseTypes = new Set<string>();
        yearsData.forEach(yearData => {
            yearData.baseTypeGroups.forEach(group => {
                if (group.totalCost > 0) {
                    activeBaseTypes.add(group.baseType);
                }
            });
        });

        return BASE_TYPE_GROUPS
            .map((group, index) => {
                const activeGroupBaseTypes = group.baseTypes.filter(bt => activeBaseTypes.has(bt));
                if (activeGroupBaseTypes.length === 0) return null;

                return {
                    label: presentBaseTypeGroupLabel(group, bt => activeBaseTypes.has(bt)),
                    data: yearsData.map(yearData =>
                        yearData.baseTypeGroups
                            .filter(g => (group.baseTypes as string[]).includes(g.baseType))
                            .reduce((sum, g) => sum + g.totalCost, 0)
                    ),
                    backgroundColor: getChartColor(index),
                    borderRadius: 0,
                    borderSkipped: false as const,
                };
            })
            .filter((d): d is NonNullable<typeof d> => d !== null);
    }, [yearsData, compareToActive, activeYearsData]);

    return (
        <>
            {showHeader && (
                <div className="draft-preview-header">
                    <div className="draft-preview-header-center">
                        <select
                            id="yearsSelect"
                            className="form-select form-select-sm filter-select"
                            value={yearsToShow}
                            onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                        >
                            {[10, 20, 30, 40, 50].map(years => (
                                <option key={years} value={years}>{years} år</option>
                            ))}
                        </select>
                        <select
                            id="costTypeSelect"
                            className="form-select form-select-sm filter-select"
                            value={costTypeFilter}
                            onChange={(e) => setCostTypeFilter(e.target.value as 'all' | 'maintenance' | 'replacement')}
                        >
                            <option value="all">Alla</option>
                            <option value="maintenance">Underhåll</option>
                            <option value="replacement">Investering</option>
                        </select>
                        {activePlan && (
                            <label className="compare-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={compareToActive}
                                    onChange={(e) => setCompareToActive(e.target.checked)}
                                />
                                <span>Jämför med aktiv underhållsplan</span>
                            </label>
                        )}
                    </div>
                    <div className="draft-preview-header-right">
                        <div className="grand-total">
                            <span className="grand-total-label">Totala kostnader</span>
                            <span className="grand-total-value">
                                {grandTotal.toLocaleString('sv-SE')} kr
                                {compareToActive && activeGrandTotal > 0 && (
                                    <span className={`cost-diff ${grandTotal - activeGrandTotal >= 0 ? 'cost-diff-positive' : 'cost-diff-negative'}`}>
                                        ({grandTotal - activeGrandTotal >= 0 ? '+' : ''}{(grandTotal - activeGrandTotal).toLocaleString('sv-SE')} kr)
                                    </span>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Inline mode: compact header with just filters and total */}
            {!showHeader && (
                <div className="draft-preview-inline-header">
                    <div className="draft-preview-inline-filters">
                        <select
                            className="form-select form-select-sm filter-select"
                            value={yearsToShow}
                            onChange={(e) => setYearsToShow(parseInt(e.target.value, 10))}
                            aria-label="Antal år"
                        >
                            {[10, 20, 30, 40, 50].map(years => (
                                <option key={years} value={years}>{years} år</option>
                            ))}
                        </select>
                        <select
                            className="form-select form-select-sm filter-select"
                            value={costTypeFilter}
                            onChange={(e) => setCostTypeFilter(e.target.value as 'all' | 'maintenance' | 'replacement')}
                            aria-label="Kostnadstyp"
                        >
                            <option value="all">Alla</option>
                            <option value="maintenance">Underhåll</option>
                            <option value="replacement">Investering</option>
                        </select>
                        {activePlan && (
                            <label className="compare-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={compareToActive}
                                    onChange={(e) => setCompareToActive(e.target.checked)}
                                />
                                <span>Jämför med aktiv underhållsplan</span>
                            </label>
                        )}
                    </div>
                    <div className="grand-total">
                        <span className="grand-total-label">Totala kostnader</span>
                        <span className="grand-total-value">
                            {compareToActive && activeGrandTotal > 0 ? (
                                <>
                                    {grandTotal.toLocaleString('sv-SE')} kr
                                    <span className={`cost-diff ${grandTotal - activeGrandTotal >= 0 ? 'cost-diff-positive' : 'cost-diff-negative'}`}>
                                        ({grandTotal - activeGrandTotal >= 0 ? '+' : ''}{(grandTotal - activeGrandTotal).toLocaleString('sv-SE')} kr)
                                    </span>
                                </>
                            ) : (
                                <>{grandTotal.toLocaleString('sv-SE')} kr</>
                            )}
                        </span>
                    </div>
                </div>
            )}

            {/* Scrollable Content */}
            <div className="draft-preview-content">
                {/* Combined Chart and Timeline Container */}
                <div className="chart-timeline-container">
                    {/* Cost Chart */}
                    <div className="cost-chart-section">
                        {yearsData.length > 0 && (
                            <Bar
                                key={`chart-${compareToActive}`}
                                data={{
                                    labels: yearsData.map(y => y.year.toString()),
                                    datasets: chartDatasets
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    animation: {
                                        duration: 500
                                    },
                                    plugins: {
                                        tooltip: {
                                            callbacks: {
                                                label: (context) => `${context.dataset.label}: ${(context.parsed.y ?? 0).toLocaleString('sv-SE')} kr`
                                            }
                                        },
                                        legend: {
                                            display: compareToActive,
                                            position: 'top' as const,
                                            labels: {
                                                boxWidth: 12,
                                                padding: 8,
                                                font: { size: 11 }
                                            }
                                        }
                                    },
                                    scales: {
                                        x: {
                                            stacked: !compareToActive,
                                            grid: { display: false },
                                            ticks: { font: { size: 14, family: 'Source Sans Pro' } }
                                        },
                                        y: {
                                            stacked: !compareToActive,
                                            grid: { display: false },
                                            ticks: {
                                                font: { size: 14, family: 'Source Sans Pro' },
                                                callback: (value) => {
                                                    const num = Number(value);
                                                    return num >= 1000 ? `${(num / 1000).toFixed(0)}k` : num;
                                                }
                                            }
                                        }
                                    }
                                }}
                                height={compareToActive ? 220 : 200}
                            />
                        )}
                    </div>

                    {/* Combined Timeline */}
                    <CombinedTimeline yearsData={yearsData} setTooltip={setTooltip} hideLegend hideHeader />

                    {/* Shared Legend */}
                    <ChartTimelineLegend yearsData={yearsData} />
                </div>

                {/* Urgent Maintenance Debt */}
                {(() => {
                    const currentYear = new Date().getFullYear();
                    const currentYearData = yearsData.find(y => y.year === currentYear);
                    const urgentCost = currentYearData?.totalCost || 0;

                    return urgentCost > 0 ? (
                        <div className="urgent-debt-section">
                            <span className="urgent-debt-label">
                                Akut underhållsskuld ({currentYear})
                                <span
                                    className="urgent-debt-info"
                                    onMouseEnter={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setTooltip({
                                            content: 'Summan av alla planerade underhålls- och investeringskostnader som förfaller under innevarande kalenderår',
                                            x: rect.right,
                                            y: rect.top + rect.height / 2
                                        });
                                    }}
                                    onMouseLeave={() => setTooltip(null)}
                                >
                                    <Info size={14} />
                                </span>
                            </span>
                            <span className="urgent-debt-value">{urgentCost.toLocaleString('sv-SE')} kr</span>
                        </div>
                    ) : null;
                })()}

                {previewGaps.length > 0 && (
                    <div className="preview-gaps-section">
                        <button
                            type="button"
                            className="preview-gaps-toggle"
                            onClick={() => setGapsExpanded(expanded => !expanded)}
                            aria-expanded={gapsExpanded}
                        >
                            {gapsExpanded ? <ChevronDown size={16} className="chevron" /> : <ChevronRight size={16} className="chevron" />}
                            <span>
                                {previewGaps.length === 1
                                    ? `1 klar komponent har inga aktiviteter inom ${yearsToShow} år`
                                    : `${previewGaps.length} klara komponenter har inga aktiviteter inom ${yearsToShow} år`}
                            </span>
                        </button>
                        {gapsExpanded && (
                            <ul className="preview-gaps-list">
                                {previewGaps.map(gap => (
                                    <li key={gap.componentKey}>
                                        <a
                                            href={`/building/${building.key}/component/${gap.componentKey}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="component-name"
                                        >
                                            {gap.componentName}
                                        </a>
                                        <span className="preview-gap-when">
                                            {gap.firstActivityYear !== null ? `första aktivitet ${gap.firstActivityYear}` : 'inga planerade aktiviteter'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {yearsData.map((yearData) => {
                    const isExpanded = expandedYears.has(yearData.year);
                    const activeYearData = activeYearsData.find(a => a.year === yearData.year);
                    const yearDiff = compareToActive && activeYearData
                        ? yearData.totalCost - activeYearData.totalCost
                        : null;

                    return (
                        <div key={yearData.year} className="year-section">
                            {/* Year Header */}
                            <div
                                className={`year-header ${isExpanded ? 'expanded' : ''} ${!yearData.hasActivities ? 'empty' : ''}`}
                                onClick={() => toggleYear(yearData.year)}
                            >
                                <div className="year-header-left">
                                    {isExpanded ? (
                                        <ChevronDown size={20} className="chevron" />
                                    ) : (
                                        <ChevronRight size={20} className="chevron" />
                                    )}
                                    <span className="year-label">{yearData.year}</span>
                                </div>
                                <div className="year-header-right">
                                    {yearData.hasActivities || (compareToActive && activeYearData && activeYearData.totalCost > 0) ? (
                                        <>
                                            {yearDiff !== null && yearDiff !== 0 && (
                                                <span className={`year-cost-diff ${yearDiff >= 0 ? 'cost-diff-positive' : 'cost-diff-negative'}`}>
                                                    {yearDiff >= 0 ? '+' : ''}{yearDiff.toLocaleString('sv-SE')} kr
                                                </span>
                                            )}
                                            <span className="year-cost">
                                                {yearData.totalCost.toLocaleString('sv-SE')} kr
                                            </span>
                                        </>
                                    ) : (
                                        <span className="no-activities-label">Inga aktiviteter</span>
                                    )}
                                </div>
                            </div>

                            {/* Year Content - Only render when expanded */}
                            {isExpanded && (
                                <div className="year-content">
                                    {yearData.hasActivities ? (
                                        yearData.baseTypeGroups.map((group) => (
                                            <div key={group.baseType} className="basetype-group">
                                                <div className="basetype-header">
                                                    <span className="basetype-label">{group.label}</span>
                                                    <span className="basetype-cost">
                                                        {group.totalCost.toLocaleString('sv-SE')} kr
                                                    </span>
                                                </div>
                                                <div className="activities-list">
                                                    {group.activities.map((activity, idx) => (
                                                        <div
                                                            key={`${activity.componentKey}-${idx}`}
                                                            className="activity-row"
                                                        >
                                                            <a
                                                                href={`/building/${building.key}/component/${activity.componentKey}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="component-name"
                                                            >
                                                                {activity.componentName}
                                                            </a>
                                                            <span className="activity-name">
                                                                {activity.activityType === 'replacement' ? 'Byte' : activity.activityName}
                                                            </span>
                                                            <span
                                                                className="cost-value"
                                                                onMouseEnter={(e) => {
                                                                    const rect = e.currentTarget.getBoundingClientRect();
                                                                    setTooltip({
                                                                        content: activity.costSource === 'custom' ? (
                                                                            <>
                                                                                <strong>{COST_SOURCE_LABELS.custom}</strong>
                                                                                {activity.costComment && (
                                                                                    <div>{activity.costComment}</div>
                                                                                )}
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <strong>{COST_SOURCE_LABELS[activity.costSource ?? 'template']}</strong>
                                                                                <div>{activity.costCalculation}</div>
                                                                                {activity.costSource === 'customRate' && activity.costComment && (
                                                                                    <div>{activity.costComment}</div>
                                                                                )}
                                                                            </>
                                                                        ),
                                                                        x: rect.right,
                                                                        y: rect.top + rect.height / 2
                                                                    });
                                                                }}
                                                                onMouseLeave={() => setTooltip(null)}
                                                            >
                                                                {activity.cost.toLocaleString('sv-SE')} kr
                                                            </span>
                                                            {activity.comment && (
                                                                <span className="preview-activity-comment">
                                                                    {activity.comment}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-year-message">
                                            Inga planerade aktiviteter detta år
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {tooltip && ReactDOM.createPortal(
                <div
                    className="preview-tooltip"
                    style={{
                        position: 'fixed',
                        top: tooltip.y,
                        left: tooltip.x + 12,
                        transform: 'translateY(-50%)'
                    }}
                >
                    {tooltip.content}
                </div>,
                document.body
            )}
        </>
    );
};
